import { useTranslation } from 'react-i18next';

import { StyleSheet, View } from '@react-pdf/renderer';
import { RoundDiagramImage } from 'PDF/components/RoundDiagramWithStepsAndPhoto/RoundDiagramImage';
import { RoundDiagramStep } from 'PDF/components/RoundDiagramWithStepsAndPhoto/RoundDiagramStep';
import { SmallHeading } from 'PDF/components/SmallHeading';
import { getColorStylesByRecord } from 'PDF/helpers/documentStyle';
import { ISalesChannelsItem } from 'PDF/pages/MarketingAndSales/types';
import { globalStyles } from 'PDF/styles';

interface ISalesChannelProps {
  data: ISalesChannelsItem[];
  isExamplePage: boolean;
}

const colorIndexes = {
  green: [2, 12, 10, 7, 8, 16],
  blue: [2, 5, 10, 0, 6, 15],
  red: [2, 5, 21, 0, 6, 16],
  blueGreen: [2, 5, 10, 0, 28, 8],
  blueRed: [2, 1, 10, 36, 8, 18],
  blueYellow: [35, 29, 36, 10, 8, 9],
};

const LONG_SUM_TEXT_LENGTH = 2500;

const sumLengths = (items: ISalesChannelProps['data']): number => {
  return items.reduce((total, item) => {
    return total + item.name.length + item.description.length;
  }, 0);
};

export const SalesChannels = ({ data, isExamplePage }: ISalesChannelProps) => {
  const colors = getColorStylesByRecord(colorIndexes);
  const { t } = useTranslation();

  return (
    <>
      {Boolean(data.length) && (
        <View wrap={false} style={globalStyles.marginBottom}>
          <SmallHeading
            heading={isExamplePage ? 'Sales Channels' : t('pdfPages.mappings.marketAnalysis.salesChannels')}
          />
          <View style={styles.container}>
            <RoundDiagramImage
              text={isExamplePage ? `SALES\nSHARE` : t('pdfPages.mappings.marketingAndSales.salesShare')}
              length={data.length}
              colors={colors}
            />
            <View style={styles.infoContainer}>
              {data.map((item, index) => (
                <RoundDiagramStep
                  smallFontSize={sumLengths(data) > LONG_SUM_TEXT_LENGTH}
                  key={index}
                  stepInfo={item}
                  stepsQuantity={data.length}
                  isLastOne={index === data.length - 1}
                  colors={colors}
                />
              ))}
            </View>
          </View>
        </View>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'row',
    alignContent: 'center',
    justifyContent: 'center',
    alignItems: 'center',
  },
  infoContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
});
